import type { Requirements } from '../types';
import { GameState } from '../game/GameState';

export interface RequirementCheckResult {
  meetsRequirements: boolean;
  needsMemory: boolean;
  memoryNeeded: number;
  missingStats: Array<{ stat: string; needed: number; have: number; withMemory?: number }>;
}

export function checkRequirements(
  requirements: Requirements | undefined,
  gameState: GameState
): RequirementCheckResult {
  if (!requirements) {
    return {
      meetsRequirements: true,
      needsMemory: false,
      memoryNeeded: 0,
      missingStats: []
    };
  }
  
  const missingStats: Array<{ stat: string; needed: number; have: number; withMemory?: number }> = [];
  let needsMemory = false;
  let totalMemoryNeeded = 0;
  
  // Check each stat requirement
  if (requirements.smarts !== undefined && requirements.smarts > 0) {
    const have = gameState.getStat('smarts');
    if (have < requirements.smarts) {
      missingStats.push({ stat: 'smarts', needed: requirements.smarts, have });
    }
  }
  
  if (requirements.tenacity !== undefined && requirements.tenacity > 0) {
    const have = gameState.getStat('tenacity');
    const memory = gameState.getResource('memory');
    const withMemory = have + memory;
    
    if (have < requirements.tenacity) {
      if (withMemory >= requirements.tenacity) {
        // Can use Memory to meet requirement
        const memoryNeeded = requirements.tenacity - have;
        needsMemory = true;
        totalMemoryNeeded = Math.max(totalMemoryNeeded, memoryNeeded);
        missingStats.push({ 
          stat: 'tenacity', 
          needed: requirements.tenacity, 
          have, 
          withMemory 
        });
      } else {
        // Even with Memory, can't meet requirement
        missingStats.push({ 
          stat: 'tenacity', 
          needed: requirements.tenacity, 
          have, 
          withMemory 
        });
      }
    } else {
      // Already meets requirement without Memory
      // Don't add to missingStats
    }
  }
  
  if (requirements.social !== undefined && requirements.social > 0) {
    const have = gameState.getStat('social');
    if (have < requirements.social) {
      missingStats.push({ stat: 'social', needed: requirements.social, have });
    }
  }
  
  // Check if we can meet all requirements (including with Memory for Tenacity)
  // If there are no missing stats, we meet requirements
  // If there are missing stats but they can be covered by Memory (for Tenacity), we meet requirements
  let meetsRequirements = missingStats.length === 0;
  
  if (missingStats.length > 0) {
    // Check if all missing stats can be covered
    meetsRequirements = true;
    for (const missing of missingStats) {
      if (missing.stat === 'tenacity' && missing.withMemory !== undefined) {
        if (missing.withMemory < missing.needed) {
          meetsRequirements = false;
          break;
        }
      } else {
        // For other stats, if they're missing, we don't meet requirements
        meetsRequirements = false;
        break;
      }
    }
  }
  
  return {
    meetsRequirements,
    needsMemory,
    memoryNeeded: totalMemoryNeeded,
    missingStats
  };
}

